import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { cn } from "@/lib/utils";
import { nok, fmtDate, num, pct } from "@/lib/format";
import { label, LEAD_STAGES, MODULE_STATUSES, TONE_DOT, toneOf } from "@/lib/labels";
import { TONE_HEX } from "@/components/map/mapUtils";

export const QuietLink = ({ onClick, children, testId }) => (
  <button type="button" onClick={onClick} className="inline-flex shrink-0 items-center gap-1 text-[12px] text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:text-foreground focus-visible:outline-none" data-testid={testId}>
    {children} <ArrowUpRight className="h-3.5 w-3.5" />
  </button>
);

/** Borderless section: title row + visual. Columns 2 and 3 get a hairline on large screens. */
export const Summary = ({ title, meta, action, children, className, testId, divided }) => (
  <section className={cn("min-w-0", divided && "lg:border-l lg:border-border/70 lg:pl-8", className)} data-testid={testId}>
    <header className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-[13.5px] font-semibold leading-5 tracking-tight">{title}</h2>
        {meta && <p className="mt-0.5 text-[12px] leading-4 text-muted-foreground">{meta}</p>}
      </div>
      {action}
    </header>
    <div className="mt-4">{children}</div>
  </section>
);

/* ---------------------------------------------------------------- fleet ---- */

const Donut = ({ segments, total, size = 132, stroke = 11, children }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full -rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
        {segments.map((s) => {
          const len = total ? (s.value / total) * circ : 0;
          const seg = <circle key={s.id} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeOpacity={s.opacity ?? 1} strokeWidth={stroke} strokeDasharray={`${Math.max(0, len - 1.5)} ${circ}`} strokeDashoffset={-acc} strokeLinecap="butt" />;
          acc += len;
          return seg;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
};

export const FleetSummary = ({ modules, divided }) => {
  const navigate = useNavigate();
  const total = modules.total || 1;
  const by = modules.by_status || {};
  const segments = MODULE_STATUSES.filter((s) => by[s] > 0).map((s) => ({ id: s, value: by[s], color: TONE_HEX[toneOf(s)] || TONE_HEX.neutral, opacity: s === "ledig" ? 0.35 : 1 }));
  const rows = MODULE_STATUSES.filter((s) => by[s] > 0).sort((a, b) => by[b] - by[a]);
  return (
    <Summary title="Flåtekapasitet" meta={`${num(modules.total)} moduler · ${modules.freeing_30d} frigis neste 30 dager`} testId="dashboard-fleet" divided={divided} action={<QuietLink onClick={() => navigate("/moduler")} testId="dashboard-fleet-link">Flåte</QuietLink>}>
      <div className="flex items-center gap-6">
        <Donut segments={segments} total={total}>
          <span className="text-[22px] font-semibold leading-7 tracking-tight tabular" data-testid="fleet-utilization">{pct(modules.utilization_pct, 0)}</span>
          <span className="text-[11px] leading-4 text-muted-foreground">utnyttelse</span>
        </Donut>
        <ul className="min-w-0 flex-1 space-y-px" data-testid="fleet-status-list">
          {rows.map((s) => (
            <li key={s}>
              <button type="button" onClick={() => navigate(`/moduler?status=${s}`)} className="-mx-2 flex h-7 w-[calc(100%+1rem)] items-center gap-2 rounded-md px-2 text-left text-[12.5px] transition-colors duration-150 hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:outline-none" data-testid={`module-status-tile-${s}`}>
                <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", TONE_DOT[toneOf(s)])} aria-hidden />
                <span className="flex-1 truncate">{label(s)}</span>
                <span className="w-10 text-right font-medium tabular">{by[s]}</span>
                <span className="w-10 text-right text-[11px] text-muted-foreground tabular">{Math.round((by[s] / total) * 100)} %</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Summary>
  );
};

/* ------------------------------------------------------------- capacity ---- */

const CapTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const w = payload[0].payload;
  return (
    <div className="rounded-lg bg-card px-3 py-2 text-[12px] shadow-pop">
      <div className="font-medium">
        {w.week} · fra {fmtDate(w.start, "d. MMM")}
      </div>
      <div className="mt-1 space-y-0.5 text-muted-foreground tabular">
        <div>
          På prosjekt: <span className="text-foreground">{w.on_project}</span>
        </div>
        <div>
          Reservert: <span className="text-foreground">{w.reserved}</span>
        </div>
        <div>
          Ledig: <span className="text-foreground">{w.available}</span> av {w.total}
        </div>
      </div>
    </div>
  );
};

export const CapacitySummary = ({ cap, divided }) => {
  const navigate = useNavigate();
  const current = cap?.weeks?.find((w) => w.is_current);
  return (
    <Summary
      title="Belastning framover"
      meta={cap?.peak ? `Neste 12 uker · topp i ${cap.peak.week} med ${cap.peak.committed} av ${cap.total} moduler bundet` : "Bundet i prosjekt og reservasjoner de neste 12 ukene"}
      testId="dashboard-capacity"
      divided={divided}
      action={<QuietLink onClick={() => navigate("/planlegging?view=capacity")} testId="dashboard-capacity-link">Planlegging</QuietLink>}
    >
      {cap?.weeks?.length ? (
        <div className="h-[132px] w-full" data-testid="capacity-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={cap.weeks} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="cap-project" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2670F2" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#2670F2" stopOpacity={0.12} />
                </linearGradient>
              </defs>
              <XAxis dataKey="week" tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} interval={1} />
              <YAxis hide domain={[0, cap.total]} />
              <Tooltip cursor={{ stroke: "rgba(16,24,40,0.15)" }} content={<CapTooltip />} />
              <ReferenceLine y={cap.total} stroke="#c9cfda" strokeDasharray="3 3" />
              {current && <ReferenceLine x={current.week} stroke="#2670F2" strokeOpacity={0.35} />}
              <Area type="monotone" dataKey="on_project" stackId="a" stroke="#2670F2" strokeWidth={1.5} fill="url(#cap-project)" isAnimationActive={false} />
              <Area type="monotone" dataKey="reserved" stackId="a" stroke="#2670F2" strokeOpacity={0.35} strokeWidth={1} fill="#2670F2" fillOpacity={0.1} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-[12px] text-muted-foreground">Ingen allokeringer registrert.</div>
      )}
      <div className="mt-2 flex items-center gap-4 text-[11.5px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-primary/70" /> På prosjekt
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-primary/20" /> Reservert
        </span>
        <span className="ml-auto tabular">Flåte {cap?.total}</span>
      </div>
    </Summary>
  );
};

/* ------------------------------------------------------------- pipeline ---- */

export const PipelineSummary = ({ leads, quotes, divided }) => {
  const navigate = useNavigate();
  const stages = LEAD_STAGES.filter((s) => !["vunnet", "tapt"].includes(s));
  const max = Math.max(1, ...stages.map((x) => (leads.pipeline[x] || {}).value || 0));
  return (
    <Summary title="Salg og ordreinngang" meta={`${leads.open} åpne henvendelser · ${quotes.pending} tilbud venter svar`} testId="dashboard-pipeline" divided={divided} action={<QuietLink onClick={() => navigate("/henvendelser")} testId="dashboard-pipeline-link">Henvendelser</QuietLink>}>
      <div className="flex items-baseline gap-2">
        <div className="text-[26px] font-semibold leading-8 tracking-tight tabular" data-testid="pipeline-value">
          {nok(leads.pipeline_value / 1e6, { decimals: 1, bare: true })} <span className="text-[13px] font-medium text-muted-foreground">MNOK</span>
        </div>
        <div className="truncate text-[12px] text-muted-foreground">estimert · {nok(quotes.pending_value)} i sendte tilbud</div>
      </div>
      <ul className="mt-3 space-y-0.5">
        {stages.map((s) => {
          const p = leads.pipeline[s] || { count: 0, value: 0 };
          return (
            <li key={s}>
              <button type="button" className="group -mx-2 block w-[calc(100%+1rem)] rounded-md px-2 py-1 text-left transition-colors duration-150 hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:outline-none" onClick={() => navigate(`/henvendelser?stage=${s}`)} data-testid={`pipeline-stage-${s}`}>
                <div className="flex items-center justify-between gap-3 text-[12.5px]">
                  <span className={cn(p.count === 0 && "text-muted-foreground")}>{label(s)}</span>
                  <span className="text-[11.5px] text-muted-foreground tabular">
                    {p.count} · {nok(p.value)}
                  </span>
                </div>
                <div className="mt-1 h-[3px] w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary/60 transition-colors duration-150 group-hover:bg-primary" style={{ width: `${p.value ? Math.max(2, (p.value / max) * 100) : 0}%` }} />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </Summary>
  );
};
